import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useState, useMemo, useCallback } from 'react';
export function ApprovalsPage() {
    const [allRequests, setAllRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('pending');
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [rejectionReason, setRejectionReason] = useState('');
    const [processing, setProcessing] = useState(false);
    const [message, setMessage] = useState(null);
    // Bulk selection state
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [showBulkRejectModal, setShowBulkRejectModal] = useState(false);
    const [bulkRejectionReason, setBulkRejectionReason] = useState('');
    // Bulk progress tracking
    const [bulkProgress, setBulkProgress] = useState(null);
    useEffect(() => {
        fetchAllRequests();
    }, []);
    // Clear selection when tab changes
    useEffect(() => {
        setSelectedIds(new Set());
    }, [filter]);
    const fetchAllRequests = async () => {
        try {
            const response = await fetch('/api/approvals/all', {
                credentials: 'include',
            });
            if (response.ok) {
                const data = await response.json();
                setAllRequests(data.requests);
            }
        }
        catch (error) {
            console.error('Failed to fetch approvals:', error);
            setMessage({ type: 'error', text: 'Failed to load approvals' });
        }
        finally {
            setLoading(false);
        }
    };
    // Derived filtered lists
    const filteredRequests = useMemo(() => {
        return allRequests.filter((r) => {
            if (filter === 'pending')
                return r.status === 'pending' && !r.error_message;
            if (filter === 'failed')
                return r.status === 'pending' && !!r.error_message;
            if (filter === 'approved')
                return r.status === 'approved' || r.status === 'executed';
            if (filter === 'rejected')
                return r.status === 'rejected';
            return true;
        });
    }, [allRequests, filter]);
    const tabCounts = useMemo(() => {
        const pending = allRequests.filter((r) => r.status === 'pending' && !r.error_message).length;
        const failed = allRequests.filter((r) => r.status === 'pending' && !!r.error_message).length;
        const approved = allRequests.filter((r) => r.status === 'approved' || r.status === 'executed').length;
        const rejected = allRequests.filter((r) => r.status === 'rejected').length;
        return { pending, failed, approved, rejected };
    }, [allRequests]);
    // Auto-select the first item whenever the filter changes or data loads
    useEffect(() => {
        if (filteredRequests.length > 0) {
            // Keep current selection if it's still in the filtered list
            const stillVisible = selectedRequest && filteredRequests.some((r) => r.id === selectedRequest.id);
            if (!stillVisible) {
                setSelectedRequest(filteredRequests[0] ?? null);
            }
        }
        else {
            setSelectedRequest(null);
        }
    }, [filteredRequests]);
    const handleApprove = async () => {
        if (!selectedRequest)
            return;
        setProcessing(true);
        try {
            const response = await fetch(`/api/approvals/${selectedRequest.id}/approve`, {
                method: 'POST',
                credentials: 'include',
            });
            const data = await response.json();
            if (response.ok) {
                setMessage({ type: 'success', text: 'Request approved and executed successfully!' });
                setSelectedRequest(null);
                fetchAllRequests();
            }
            else {
                setMessage({ type: 'error', text: data.message || 'Failed to approve request' });
            }
        }
        catch (error) {
            console.error('Error approving request:', error);
            setMessage({ type: 'error', text: 'Failed to approve request' });
        }
        finally {
            setProcessing(false);
            setTimeout(() => setMessage(null), 5000);
        }
    };
    const handleReject = async () => {
        if (!selectedRequest || !rejectionReason.trim()) {
            setMessage({ type: 'error', text: 'Please provide a rejection reason' });
            return;
        }
        setProcessing(true);
        try {
            const response = await fetch(`/api/approvals/${selectedRequest.id}/reject`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ reason: rejectionReason }),
            });
            const data = await response.json();
            if (response.ok) {
                setMessage({ type: 'success', text: 'Request rejected successfully' });
                setSelectedRequest(null);
                setRejectionReason('');
                fetchAllRequests();
            }
            else {
                setMessage({ type: 'error', text: data.message || 'Failed to reject request' });
            }
        }
        catch (error) {
            console.error('Error rejecting request:', error);
            setMessage({ type: 'error', text: 'Failed to reject request' });
        }
        finally {
            setProcessing(false);
            setTimeout(() => setMessage(null), 5000);
        }
    };
    const handleDismiss = async () => {
        if (!selectedRequest)
            return;
        setProcessing(true);
        try {
            const response = await fetch(`/api/approvals/${selectedRequest.id}/reject`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ reason: 'Dismissed — stale failed request' }),
            });
            const data = await response.json();
            if (response.ok) {
                setMessage({ type: 'success', text: 'Request dismissed' });
                setSelectedRequest(null);
                fetchAllRequests();
            }
            else {
                setMessage({ type: 'error', text: data.message || 'Failed to dismiss request' });
            }
        }
        catch (error) {
            console.error('Error dismissing request:', error);
            setMessage({ type: 'error', text: 'Failed to dismiss request' });
        }
        finally {
            setProcessing(false);
            setTimeout(() => setMessage(null), 5000);
        }
    };
    const handleBulkDismiss = useCallback(async () => {
        if (selectedIds.size === 0)
            return;
        const ids = Array.from(selectedIds);
        setBulkProgress({
            action: 'reject',
            total: ids.length,
            completed: 0,
            succeeded: 0,
            failed: 0,
            currentIssue: '',
            errors: [],
        });
        for (const id of ids) {
            const req = allRequests.find(r => r.id === id);
            setBulkProgress(prev => prev ? { ...prev, currentIssue: req?.metadata?.issueKey || req?.field_name || id } : prev);
            try {
                const response = await fetch(`/api/approvals/${id}/reject`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    credentials: 'include',
                    body: JSON.stringify({ reason: 'Dismissed — stale failed request' }),
                });
                if (response.ok) {
                    setBulkProgress(prev => prev ? { ...prev, completed: prev.completed + 1, succeeded: prev.succeeded + 1 } : prev);
                }
                else {
                    const data = await response.json().catch(() => ({}));
                    setBulkProgress(prev => prev ? {
                        ...prev,
                        completed: prev.completed + 1,
                        failed: prev.failed + 1,
                        errors: [...prev.errors, { id, error: data.message || 'Failed' }],
                    } : prev);
                }
            }
            catch {
                setBulkProgress(prev => prev ? {
                    ...prev,
                    completed: prev.completed + 1,
                    failed: prev.failed + 1,
                    errors: [...prev.errors, { id, error: 'Network error' }],
                } : prev);
            }
        }
        setSelectedIds(new Set());
        fetchAllRequests();
        setTimeout(() => setBulkProgress(null), 3000);
    }, [selectedIds, allRequests, fetchAllRequests]);
    const handleRetry = async () => {
        if (!selectedRequest)
            return;
        setProcessing(true);
        try {
            const response = await fetch(`/api/approvals/${selectedRequest.id}/retry`, {
                method: 'POST',
                credentials: 'include',
            });
            const data = await response.json();
            if (response.ok) {
                setMessage({ type: 'success', text: 'Request retried and executed successfully!' });
                setSelectedRequest(null);
                fetchAllRequests();
            }
            else {
                setMessage({ type: 'error', text: data.message || 'Failed to retry request' });
            }
        }
        catch (error) {
            console.error('Error retrying request:', error);
            setMessage({ type: 'error', text: 'Failed to retry request' });
        }
        finally {
            setProcessing(false);
            setTimeout(() => setMessage(null), 5000);
        }
    };
    // Bulk retry — processes one at a time with live progress
    const handleBulkRetry = useCallback(async () => {
        if (selectedIds.size === 0)
            return;
        const ids = Array.from(selectedIds);
        setProcessing(true);
        setBulkProgress({
            action: 'approve',
            total: ids.length,
            completed: 0,
            succeeded: 0,
            failed: 0,
            currentIssue: '',
            errors: [],
        });
        let succeeded = 0;
        let failed = 0;
        const errors = [];
        for (let i = 0; i < ids.length; i++) {
            const id = ids[i];
            const req = allRequests.find(r => r.id === id);
            const label = req?.metadata?.issueKey || req?.field_name || id.slice(0, 8);
            setBulkProgress(prev => prev ? {
                ...prev,
                completed: i,
                currentIssue: label,
            } : prev);
            try {
                const response = await fetch(`/api/approvals/${id}/retry`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    credentials: 'include',
                });
                if (response.ok) {
                    succeeded++;
                }
                else {
                    const data = await response.json();
                    failed++;
                    errors.push({ id, error: data.message || 'Failed' });
                }
            }
            catch (error) {
                failed++;
                errors.push({ id, error: error instanceof Error ? error.message : 'Network error' });
            }
            setBulkProgress(prev => prev ? {
                ...prev,
                completed: i + 1,
                succeeded,
                failed,
                errors,
            } : prev);
        }
        if (failed > 0) {
            setMessage({
                type: 'error',
                text: `${succeeded} retried, ${failed} failed: ${errors.map(e => e.error).slice(0, 3).join('; ')}`,
            });
        }
        else {
            setMessage({
                type: 'success',
                text: `${succeeded} request(s) retried and executed successfully`,
            });
        }
        setSelectedIds(new Set());
        setSelectedRequest(null);
        setProcessing(false);
        setBulkProgress(null);
        fetchAllRequests();
        setTimeout(() => setMessage(null), 8000);
    }, [selectedIds, allRequests]);
    // Bulk approve — processes one at a time with live progress
    const handleBulkApprove = useCallback(async () => {
        if (selectedIds.size === 0)
            return;
        const ids = Array.from(selectedIds);
        setProcessing(true);
        setBulkProgress({
            action: 'approve',
            total: ids.length,
            completed: 0,
            succeeded: 0,
            failed: 0,
            currentIssue: '',
            errors: [],
        });
        let succeeded = 0;
        let failed = 0;
        const errors = [];
        for (let i = 0; i < ids.length; i++) {
            const id = ids[i];
            // Find the request to show its issue key
            const req = allRequests.find(r => r.id === id);
            const label = req?.metadata?.issueKey || req?.field_name || id.slice(0, 8);
            setBulkProgress(prev => prev ? {
                ...prev,
                completed: i,
                currentIssue: label,
            } : prev);
            try {
                const response = await fetch(`/api/approvals/${id}/approve`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    credentials: 'include',
                });
                if (response.ok) {
                    succeeded++;
                }
                else {
                    const data = await response.json();
                    failed++;
                    errors.push({ id, error: data.message || 'Failed' });
                }
            }
            catch (error) {
                failed++;
                errors.push({ id, error: error instanceof Error ? error.message : 'Network error' });
            }
            setBulkProgress(prev => prev ? {
                ...prev,
                completed: i + 1,
                succeeded,
                failed,
                errors,
            } : prev);
        }
        // Done
        if (failed > 0) {
            setMessage({
                type: 'error',
                text: `${succeeded} approved, ${failed} failed: ${errors.map(e => e.error).slice(0, 3).join('; ')}`,
            });
        }
        else {
            setMessage({
                type: 'success',
                text: `${succeeded} request(s) approved and executed successfully`,
            });
        }
        setSelectedIds(new Set());
        setSelectedRequest(null);
        setProcessing(false);
        setBulkProgress(null);
        fetchAllRequests();
        setTimeout(() => setMessage(null), 8000);
    }, [selectedIds, allRequests]);
    // Bulk reject — processes one at a time with live progress
    const handleBulkReject = useCallback(async () => {
        if (selectedIds.size === 0 || !bulkRejectionReason.trim())
            return;
        const ids = Array.from(selectedIds);
        const reason = bulkRejectionReason.trim();
        setShowBulkRejectModal(false);
        setProcessing(true);
        setBulkProgress({
            action: 'reject',
            total: ids.length,
            completed: 0,
            succeeded: 0,
            failed: 0,
            currentIssue: '',
            errors: [],
        });
        let succeeded = 0;
        let failed = 0;
        const errors = [];
        for (let i = 0; i < ids.length; i++) {
            const id = ids[i];
            const req = allRequests.find(r => r.id === id);
            const label = req?.metadata?.issueKey || req?.field_name || id.slice(0, 8);
            setBulkProgress(prev => prev ? {
                ...prev,
                completed: i,
                currentIssue: label,
            } : prev);
            try {
                const response = await fetch(`/api/approvals/${id}/reject`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    credentials: 'include',
                    body: JSON.stringify({ reason }),
                });
                if (response.ok) {
                    succeeded++;
                }
                else {
                    const data = await response.json();
                    failed++;
                    errors.push({ id, error: data.message || 'Failed' });
                }
            }
            catch (error) {
                failed++;
                errors.push({ id, error: error instanceof Error ? error.message : 'Network error' });
            }
            setBulkProgress(prev => prev ? {
                ...prev,
                completed: i + 1,
                succeeded,
                failed,
                errors,
            } : prev);
        }
        // Done
        if (failed > 0) {
            setMessage({
                type: 'error',
                text: `${succeeded} rejected, ${failed} failed: ${errors.map(e => e.error).slice(0, 3).join('; ')}`,
            });
        }
        else {
            setMessage({
                type: 'success',
                text: `${succeeded} request(s) rejected successfully`,
            });
        }
        setSelectedIds(new Set());
        setSelectedRequest(null);
        setBulkRejectionReason('');
        setProcessing(false);
        setBulkProgress(null);
        fetchAllRequests();
        setTimeout(() => setMessage(null), 8000);
    }, [selectedIds, bulkRejectionReason, allRequests]);
    // Checkbox handlers
    const toggleSelect = useCallback((id) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) {
                next.delete(id);
            }
            else {
                next.add(id);
            }
            return next;
        });
    }, []);
    const toggleSelectAll = useCallback(() => {
        if (selectedIds.size === filteredRequests.length) {
            setSelectedIds(new Set());
        }
        else {
            setSelectedIds(new Set(filteredRequests.map((r) => r.id)));
        }
    }, [selectedIds.size, filteredRequests]);
    const formatFieldName = (fieldName) => {
        return fieldName
            .split('_')
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
    };
    const formatRelativeTime = (dateString) => {
        const now = new Date();
        const date = new Date(dateString);
        const diffMs = now.getTime() - date.getTime();
        const diffMin = Math.floor(diffMs / 60000);
        if (diffMin < 1)
            return 'just now';
        if (diffMin < 60)
            return `${diffMin} min ago`;
        const diffHrs = Math.floor(diffMin / 60);
        if (diffHrs < 24)
            return `${diffHrs}h ago`;
        const diffDays = Math.floor(diffHrs / 24);
        if (diffDays === 1)
            return 'yesterday';
        if (diffDays < 30)
            return `${diffDays}d ago`;
        return date.toLocaleDateString();
    };
    const getDisplayValue = (request, which) => {
        if (request.field_name === 'assignee') {
            if (which === 'new')
                return request.metadata.assigneeDisplayName || request.new_value;
            return request.old_value;
        }
        // Show original_estimate as duration instead of raw seconds
        if (request.field_name === 'original_estimate') {
            const raw = which === 'old' ? request.old_value : request.new_value;
            if (raw) {
                const secs = parseInt(raw);
                if (!isNaN(secs) && secs > 0) {
                    const h = Math.floor(secs / 3600);
                    const m = Math.floor((secs % 3600) / 60);
                    const parts = [];
                    if (h > 0)
                        parts.push(`${h}h`);
                    if (m > 0)
                        parts.push(`${m}m`);
                    return parts.join(' ') || '0m';
                }
            }
            return raw;
        }
        // Truncate long description values for display
        if (request.field_name === 'description') {
            const raw = which === 'old' ? request.old_value : request.new_value;
            if (raw && raw.length > 100)
                return raw.substring(0, 100) + '...';
            return raw;
        }
        if (which === 'old')
            return request.metadata.oldCategoryDisplay || request.old_value;
        return request.metadata.newCategoryDisplay || request.new_value;
    };
    const computeValueChange = (request) => {
        const oldVal = getDisplayValue(request, 'old');
        const newVal = getDisplayValue(request, 'new');
        // Try numeric diff for hours-based fields
        const oldNum = parseFloat(oldVal || '0');
        const newNum = parseFloat(newVal || '0');
        if (!isNaN(oldNum) && !isNaN(newNum) && (oldVal || '').match(/^\d/)) {
            const diff = newNum - oldNum;
            const sign = diff >= 0 ? '+' : '';
            return `${sign}${diff} h`;
        }
        // Non-numeric: show the new value
        return newVal || '';
    };
    const getSystemBadges = (request) => {
        const systems = [];
        if (request.target_system === 'jira')
            systems.push('JIRA');
        if (request.target_system === 'tempo')
            systems.push('TEMPO');
        return systems.join(' + ');
    };
    const isPendingTab = filter === 'pending';
    const isFailedTab = filter === 'failed';
    const isActionableTab = isPendingTab || isFailedTab;
    const allSelected = filteredRequests.length > 0 && selectedIds.size === filteredRequests.length;
    const someSelected = selectedIds.size > 0 && selectedIds.size < filteredRequests.length;
    if (loading) {
        return (_jsx("div", { className: "flex items-center justify-center py-20", children: _jsxs("div", { className: "text-center", children: [_jsx("div", { className: "inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" }), _jsx("p", { className: "mt-4 text-body-muted", children: "Loading approvals..." })] }) }));
    }
    return (_jsxs("div", { className: "flex flex-col h-full", children: [message && (_jsx("div", { className: `mb-4 px-4 py-3 rounded-xl text-sm font-normal ${message.type === 'success'
                    ? 'bg-green-50 text-green-800 border border-green-200'
                    : 'bg-red-50 text-red-800 border border-red-200'}`, children: message.text })), _jsx("div", { className: "flex items-center gap-2 mb-6", children: ['pending', 'failed', 'approved', 'rejected'].map((tab) => {
                    const count = tabCounts[tab];
                    const isActive = filter === tab;
                    return (_jsxs("button", { onClick: () => setFilter(tab), className: `px-5 py-2 text-sm font-normal transition-colors ${isActive
                            ? 'bg-ink text-white rounded-pill'
                            : 'text-body-muted hover:text-body'}`, children: [tab.charAt(0).toUpperCase() + tab.slice(1), count > 0 && (_jsx("span", { className: `ml-1.5 ${isActive ? 'text-white/70' : 'text-body-muted'}`, children: count }))] }, tab));
                }) }), filteredRequests.length === 0 ? (_jsxs("div", { className: "bg-white rounded-xl shadow-card p-12 text-center", children: [_jsx("svg", { className: "mx-auto h-12 w-12 text-gray-300", fill: "none", viewBox: "0 0 24 24", stroke: "currentColor", children: _jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 1.5, d: "M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" }) }), _jsxs("h3", { className: "mt-3 text-sm font-bold text-body", children: ["No ", filter, " requests"] }), _jsx("p", { className: "mt-1 text-sm text-body-muted", children: filter === 'pending'
                            ? 'All requests have been processed.'
                            : `No ${filter} requests to display.` })] })) : (_jsxs("div", { className: "grid grid-cols-2 gap-6 min-h-0 flex-1", children: [_jsxs("div", { className: "bg-white rounded-xl shadow-card overflow-y-auto flex flex-col", children: [isActionableTab && filteredRequests.length > 0 && (_jsxs("div", { className: "px-5 py-2.5 border-b border-gray-100 flex items-center gap-3 bg-surface-muted/50 sticky top-0 z-10", children: [_jsx("input", { type: "checkbox", checked: allSelected, ref: (el) => { if (el)
                                            el.indeterminate = someSelected; }, onChange: toggleSelectAll, className: "w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 cursor-pointer" }), _jsx("span", { className: "text-xs text-body-muted", children: selectedIds.size > 0
                                            ? `${selectedIds.size} of ${filteredRequests.length} selected`
                                            : 'Select all' })] })), filteredRequests.map((request) => {
                                const isSelected = selectedRequest?.id === request.id;
                                const isChecked = selectedIds.has(request.id);
                                return (_jsxs("div", { className: `flex items-start gap-3 w-full text-left px-5 py-4 border-b border-gray-100 transition-colors last:border-b-0 cursor-pointer ${isSelected ? 'bg-primary-50' : 'hover:bg-surface-muted'}`, onClick: () => {
                                        setSelectedRequest(request);
                                        setRejectionReason('');
                                    }, children: [isActionableTab && (_jsx("input", { type: "checkbox", checked: isChecked, onChange: (e) => {
                                                e.stopPropagation();
                                                toggleSelect(request.id);
                                            }, onClick: (e) => e.stopPropagation(), className: "w-4 h-4 mt-0.5 rounded border-gray-300 text-primary-600 focus:ring-primary-500 cursor-pointer flex-shrink-0" })), _jsxs("div", { className: "flex-1 min-w-0", children: [_jsxs("div", { className: "flex items-center justify-between", children: [_jsxs("div", { className: "flex items-center gap-3 min-w-0", children: [_jsx("span", { className: "text-sm font-bold text-body-strong truncate", children: request.jira_project_key }), _jsx("span", { className: "text-sm text-body-muted truncate", children: formatFieldName(request.field_name) })] }), _jsx("span", { className: "text-sm font-bold text-body-strong whitespace-nowrap ml-3", children: computeValueChange(request) })] }), _jsxs("div", { className: "mt-1 text-xs text-body-muted", children: [request.metadata.issueKey && (_jsxs(_Fragment, { children: [_jsx("span", { className: "font-medium", children: request.metadata.issueKey }), _jsx("span", { className: "mx-1.5", children: "\u00B7" })] })), request.requested_by_name, _jsx("span", { className: "mx-1.5", children: "\u00B7" }), formatRelativeTime(request.requested_at), request.retry_count > 0 && (_jsxs(_Fragment, { children: [_jsx("span", { className: "mx-1.5", children: "\u00B7" }), _jsxs("span", { className: "text-amber-600", children: [request.retry_count, " retries"] })] }))] }), request.error_message && (_jsx("div", { className: "mt-1 text-xs text-red-600 truncate", children: request.error_message }))] })] }, request.id));
                            })] }), selectedRequest ? (_jsxs("div", { className: "bg-white rounded-xl shadow-card overflow-y-auto flex flex-col", children: [_jsxs("div", { className: "p-6 flex-1", children: [_jsxs("div", { className: "flex items-center justify-between mb-1", children: [_jsxs("div", { className: "flex items-center gap-2 text-sm text-body-muted", children: [_jsx("span", { className: "font-bold text-body-strong", children: selectedRequest.jira_project_key }), _jsx("span", { children: "\u00B7" }), _jsx("span", { className: "uppercase tracking-wide text-xs", children: getSystemBadges(selectedRequest) })] }), selectedRequest.status === 'pending' && !selectedRequest.error_message && (_jsx("span", { className: "text-xs font-normal text-primary-500", children: "Awaiting your decision" })), selectedRequest.status === 'pending' && !!selectedRequest.error_message && (_jsx("span", { className: "text-xs font-bold text-red-600 bg-red-50 px-2 py-0.5 rounded-full", children: "Execution Failed" })), selectedRequest.status === 'approved' || selectedRequest.status === 'executed' ? (_jsx("span", { className: "text-xs font-normal text-success", children: "Approved" })) : null, selectedRequest.status === 'rejected' && (_jsx("span", { className: "text-xs font-normal text-danger", children: "Rejected" }))] }), _jsxs("h2", { className: "text-lg font-bold text-body-strong mt-3", children: [formatFieldName(selectedRequest.field_name), " change"] }), selectedRequest.metadata.issueKey && (_jsxs("p", { className: "text-sm text-body-muted mt-1", children: [_jsx("span", { className: "font-bold text-body", children: selectedRequest.metadata.issueKey }), selectedRequest.metadata.summary && (_jsxs(_Fragment, { children: [" \u2014 ", selectedRequest.metadata.summary] }))] })), _jsxs("a", { href: `/projects/${selectedRequest.project_id}`, className: "inline-flex items-center gap-1 text-sm text-primary-500 hover:underline mt-1 mb-6", children: [selectedRequest.project_name, _jsx("svg", { className: "w-3.5 h-3.5", fill: "none", viewBox: "0 0 24 24", stroke: "currentColor", strokeWidth: 2, children: _jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", d: "M9 5l7 7-7 7" }) })] }), _jsxs("div", { className: "flex items-stretch gap-3 mb-6", children: [_jsxs("div", { className: "flex-1 bg-surface-muted rounded-xl p-4", children: [_jsx("span", { className: "block text-[10px] font-bold uppercase tracking-widest text-body-muted mb-2", children: "Current" }), _jsx("span", { className: "text-xl font-bold text-body-strong", children: getDisplayValue(selectedRequest, 'old') || 'None' })] }), _jsx("div", { className: "flex items-center", children: _jsx("svg", { className: "w-5 h-5 text-body-muted", fill: "none", viewBox: "0 0 24 24", stroke: "currentColor", strokeWidth: 2, children: _jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", d: "M9 5l7 7-7 7" }) }) }), _jsxs("div", { className: "flex-1 border-2 border-primary-500 rounded-xl p-4", children: [_jsx("span", { className: "block text-[10px] font-bold uppercase tracking-widest text-body-muted mb-2", children: "Proposed" }), _jsx("span", { className: "text-xl font-bold text-body-strong", children: getDisplayValue(selectedRequest, 'new') || selectedRequest.new_value })] })] }), selectedRequest.metadata.impact && (_jsxs("div", { className: "mb-5", children: [_jsx("h4", { className: "text-xs font-bold uppercase tracking-widest text-body-muted mb-1", children: "Impact" }), _jsx("p", { className: "text-sm text-body leading-relaxed", children: selectedRequest.metadata.impact })] })), selectedRequest.metadata.reason && (_jsxs("div", { className: "mb-5", children: [_jsx("h4", { className: "text-xs font-bold uppercase tracking-widest text-body-muted mb-1", children: "Reason" }), _jsx("p", { className: "text-sm text-body leading-relaxed", children: selectedRequest.metadata.reason })] })), _jsxs("div", { className: "mb-5", children: [_jsx("h4", { className: "text-xs font-bold uppercase tracking-widest text-body-muted mb-1", children: "Requested by" }), _jsxs("p", { className: "text-sm text-body", children: [selectedRequest.requested_by_name, _jsxs("span", { className: "text-body-muted", children: [_jsx("span", { className: "mx-1.5", children: "\u00B7" }), formatRelativeTime(selectedRequest.requested_at)] })] })] }), selectedRequest.status === 'rejected' && selectedRequest.rejection_reason && (_jsxs("div", { className: "mb-5", children: [_jsx("h4", { className: "text-xs font-bold uppercase tracking-widest text-body-muted mb-1", children: "Rejection reason" }), _jsx("p", { className: "text-sm text-body leading-relaxed", children: selectedRequest.rejection_reason })] })), selectedRequest.reviewed_at && selectedRequest.reviewed_by_name && (_jsxs("div", { className: "mb-5", children: [_jsx("h4", { className: "text-xs font-bold uppercase tracking-widest text-body-muted mb-1", children: "Reviewed by" }), _jsxs("p", { className: "text-sm text-body", children: [selectedRequest.reviewed_by_name, _jsxs("span", { className: "text-body-muted", children: [_jsx("span", { className: "mx-1.5", children: "\u00B7" }), formatRelativeTime(selectedRequest.reviewed_at)] })] })] })), selectedRequest.error_message && (_jsxs("div", { className: "mb-5", children: [_jsx("h4", { className: "text-xs font-bold uppercase tracking-widest text-red-500 mb-1", children: "Execution Error" }), _jsxs("div", { className: "bg-red-50 border border-red-200 rounded-lg p-3", children: [_jsx("p", { className: "text-sm text-red-800 leading-relaxed break-words", children: selectedRequest.error_message }), selectedRequest.retry_count > 0 && (_jsxs("p", { className: "text-xs text-red-500 mt-2", children: ["Failed after ", selectedRequest.retry_count, " retry attempt", selectedRequest.retry_count > 1 ? 's' : '', " with exponential backoff"] }))] })] })), selectedRequest.status === 'pending' && selectedIds.size === 0 && (_jsxs("div", { className: "mb-2", children: [_jsx("label", { htmlFor: "detail-rejection-reason", className: "text-xs font-bold uppercase tracking-widest text-body-muted mb-1 block", children: "Rejection reason (required to reject)" }), _jsx("textarea", { id: "detail-rejection-reason", value: rejectionReason, onChange: (e) => setRejectionReason(e.target.value), rows: 2, className: "w-full px-3 py-2 text-sm border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none resize-none", placeholder: "Explain why this request should be rejected..." })] }))] }), selectedRequest.status === 'pending' && !selectedRequest.error_message && selectedIds.size === 0 && (_jsxs("div", { className: "border-t border-gray-100 px-6 py-4 flex items-center justify-end gap-3", children: [_jsx("button", { onClick: handleReject, disabled: processing || !rejectionReason.trim(), className: "px-5 py-2.5 text-sm font-bold border border-danger text-danger rounded-pill hover:bg-red-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed", children: processing ? 'Rejecting...' : 'Reject' }), _jsx("button", { onClick: handleApprove, disabled: processing, className: "px-5 py-2.5 text-sm font-bold bg-primary-500 text-white rounded-pill hover:bg-primary-600 transition-colors disabled:opacity-50", children: processing ? 'Approving...' : 'Approve' })] })), selectedRequest.status === 'pending' && !!selectedRequest.error_message && selectedIds.size === 0 && (_jsxs("div", { className: "border-t border-gray-100 px-6 py-4 flex items-center justify-end gap-3", children: [_jsx("button", { onClick: handleDismiss, disabled: processing, className: "px-5 py-2.5 text-sm font-bold border border-gray-300 text-text-secondary rounded-pill hover:bg-gray-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed", children: processing ? 'Dismissing...' : 'Dismiss' }), _jsx("button", { onClick: handleRetry, disabled: processing, className: "px-5 py-2.5 text-sm font-bold bg-amber-500 text-white rounded-pill hover:bg-amber-600 transition-colors disabled:opacity-50", children: processing ? 'Retrying...' : 'Retry' })] }))] })) : (_jsx("div", { className: "bg-white rounded-xl shadow-card flex items-center justify-center", children: _jsx("p", { className: "text-sm text-body-muted", children: "Select a request to view details" }) }))] })), selectedIds.size > 0 && isActionableTab && (_jsx("div", { className: "fixed bottom-6 left-1/2 -translate-x-1/2 z-30 bg-ink text-white rounded-xl shadow-lg px-5 py-3 flex flex-col gap-2 min-w-[400px]", children: bulkProgress ? (_jsxs(_Fragment, { children: [_jsxs("div", { className: "flex items-center justify-between", children: [_jsxs("span", { className: "text-sm font-medium", children: [bulkProgress.action === 'approve' ? (isFailedTab ? 'Retrying' : 'Approving') : (isFailedTab ? 'Dismissing' : 'Rejecting'), " ", bulkProgress.completed, "/", bulkProgress.total] }), _jsxs("span", { className: "text-xs text-white/70", children: [bulkProgress.succeeded > 0 && _jsxs("span", { className: "text-green-400", children: [bulkProgress.succeeded, " done"] }), bulkProgress.failed > 0 && _jsxs("span", { className: "text-red-400 ml-2", children: [bulkProgress.failed, " failed"] })] })] }), _jsx("div", { className: "w-full bg-white/20 rounded-full h-2", children: _jsx("div", { className: `h-2 rounded-full transition-all duration-300 ${bulkProgress.failed > 0 ? 'bg-yellow-400' : 'bg-green-400'}`, style: { width: `${(bulkProgress.completed / bulkProgress.total) * 100}%` } }) }), bulkProgress.currentIssue && bulkProgress.completed < bulkProgress.total && (_jsxs("div", { className: "text-xs text-white/60 flex items-center gap-1.5", children: [_jsxs("svg", { className: "w-3 h-3 animate-spin", fill: "none", viewBox: "0 0 24 24", children: [_jsx("circle", { className: "opacity-25", cx: "12", cy: "12", r: "10", stroke: "currentColor", strokeWidth: "4" }), _jsx("path", { className: "opacity-75", fill: "currentColor", d: "M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" })] }), bulkProgress.action === 'approve' ? (isFailedTab ? 'Retrying' : 'Executing') : (isFailedTab ? 'Dismissing' : 'Rejecting'), " ", bulkProgress.currentIssue, "..."] })), bulkProgress.errors.length > 0 && (_jsx("div", { className: "text-xs text-red-300 max-h-16 overflow-y-auto", children: bulkProgress.errors.map((e, i) => (_jsx("div", { className: "truncate", children: e.error }, i))) }))] })) : (_jsxs("div", { className: "flex items-center gap-4", children: [_jsxs("span", { className: "text-sm font-medium whitespace-nowrap", children: [selectedIds.size, " selected"] }), _jsx("div", { className: "w-px h-5 bg-white/20" }), _jsxs("button", { onClick: isFailedTab ? handleBulkRetry : handleBulkApprove, disabled: processing, className: `text-xs px-4 py-1.5 rounded-lg transition-colors font-bold disabled:opacity-50 ${isFailedTab
                                ? 'bg-amber-500 hover:bg-amber-600'
                                : 'bg-green-600 hover:bg-green-700'}`, children: [isFailedTab ? 'Retry' : 'Approve', " ", selectedIds.size] }), isFailedTab ? (_jsxs("button", { onClick: handleBulkDismiss, disabled: processing, className: "text-xs px-4 py-1.5 bg-gray-500 hover:bg-gray-600 rounded-lg transition-colors font-bold disabled:opacity-50", children: ["Dismiss ", selectedIds.size] })) : (_jsxs("button", { onClick: () => setShowBulkRejectModal(true), disabled: processing, className: "text-xs px-4 py-1.5 bg-red-600 hover:bg-red-700 rounded-lg transition-colors font-bold disabled:opacity-50", children: ["Reject ", selectedIds.size] })), _jsx("div", { className: "w-px h-5 bg-white/20" }), _jsxs("button", { onClick: () => setSelectedIds(new Set()), className: "text-xs text-white/70 hover:text-white flex items-center gap-1 transition-colors", children: [_jsx("svg", { className: "w-3.5 h-3.5", fill: "none", viewBox: "0 0 24 24", stroke: "currentColor", children: _jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 2, d: "M6 18L18 6M6 6l12 12" }) }), "Clear"] })] })) })), showBulkRejectModal && (_jsxs(_Fragment, { children: [_jsx("div", { className: "fixed inset-0 bg-black/30 z-40", onClick: () => setShowBulkRejectModal(false) }), _jsx("div", { className: "fixed inset-0 z-50 flex items-center justify-center p-4", children: _jsxs("div", { className: "bg-white rounded-xl shadow-xl w-full max-w-md", onClick: (e) => e.stopPropagation(), children: [_jsxs("div", { className: "p-6", children: [_jsxs("h3", { className: "text-lg font-bold text-body-strong mb-1", children: ["Reject ", selectedIds.size, " request", selectedIds.size > 1 ? 's' : ''] }), _jsx("p", { className: "text-sm text-body-muted mb-4", children: "Provide a reason for rejecting these requests. The same reason will be applied to all selected requests." }), _jsx("textarea", { value: bulkRejectionReason, onChange: (e) => setBulkRejectionReason(e.target.value), rows: 3, className: "w-full px-3 py-2 text-sm border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none resize-none", placeholder: "Explain why these requests should be rejected...", autoFocus: true })] }), _jsxs("div", { className: "border-t border-gray-100 px-6 py-4 flex items-center justify-end gap-3", children: [_jsx("button", { onClick: () => {
                                                setShowBulkRejectModal(false);
                                                setBulkRejectionReason('');
                                            }, className: "px-5 py-2.5 text-sm font-bold text-body-muted hover:text-body transition-colors", children: "Cancel" }), _jsx("button", { onClick: handleBulkReject, disabled: processing || !bulkRejectionReason.trim(), className: "px-5 py-2.5 text-sm font-bold bg-red-600 text-white rounded-pill hover:bg-red-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed", children: processing ? 'Rejecting...' : `Reject ${selectedIds.size}` })] })] }) })] }))] }));
}
//# sourceMappingURL=ApprovalsPage.js.map